import React, { useState, useEffect, useCallback } from "react";
import {
  Card, Row, Col, Spin, Empty, Select, DatePicker, Typography, Table, Tag, message, Segmented,
} from "antd";
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
} from "recharts";
import dayjs from "dayjs";
import { getDetectionHealthOverview, getCameras } from "../network/api";
import InfoHint from "./common/InfoHint";
import { useAuth } from "../context/AuthContext";
import DetectionReview from "./detectionReview";

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;

// Same palette the rest of the analytics pages use, so this reads as one product.
const GOOD = "#52c41a";
const WARN = "#faad14";
const CRIT = "#ff4d4f";
const ACCENT = "#13a8a8";
const MUTED = "#8c9bab";
const EMPTY = "#e6e8eb";

const dayWindow = (range) => ({
  from: range?.[0]?.startOf("day").toISOString(),
  to: range?.[1]?.endOf("day").toISOString(),
});
const pctOf = (x) => (x == null ? "—" : `${Math.round(x * 100)}%`);
const num = (n) => (n == null ? "—" : Number(n).toLocaleString("en-US"));

const ISSUE = {
  blind: { label: "Blind — no fridge", color: "error" },
  over_blur: { label: "Over-blurring", color: "warning" },
  very_low: { label: "Very low yield", color: "processing" },
  low: { label: "Low yield", color: "processing" },
};

// 10x10 grid, one square per photo out of 100 — the most literal way to show a proportion.
function Waffle({ segs }) {
  const cells = [];
  segs.forEach((s) => {
    for (let i = 0; i < s.n; i++) cells.push(s.color);
  });
  while (cells.length < 100) cells.push(EMPTY);
  return (
    <div style={{ display: "grid", gridTemplateColumns: "repeat(10, 1fr)", gap: 4, width: 190 }}>
      {cells.slice(0, 100).map((c, i) => (
        <div key={i} style={{ aspectRatio: "1 / 1", borderRadius: 4, background: c }} />
      ))}
    </div>
  );
}

function Keys({ items }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 10, minWidth: 170 }}>
      {items.map((it, i) => (
        <div key={i} style={{ display: "flex", gap: 10, alignItems: "flex-start" }}>
          <span style={{ width: 14, height: 14, borderRadius: 4, background: it.color, marginTop: 3, flex: "none" }} />
          <div>
            <div>
              <span style={{ fontSize: 20, fontWeight: 700 }}>{it.big}</span>{" "}
              <Text type="secondary" style={{ fontSize: 12 }}>/ 100</Text>
            </div>
            <div style={{ fontWeight: 600 }}>{it.label}</div>
            <Text type="secondary" style={{ fontSize: 12 }}>{it.sub}</Text>
          </div>
        </div>
      ))}
    </div>
  );
}

function Tile({ value, label, sub, color }) {
  return (
    <Card size="small" style={{ borderLeft: `4px solid ${color}`, height: "100%" }}>
      <div style={{ fontSize: 34, fontWeight: 700, lineHeight: 1 }}>{value}</div>
      <div style={{ fontWeight: 600, marginTop: 6 }}>{label}</div>
      <Text type="secondary" style={{ fontSize: 12 }}>{sub}</Text>
    </Card>
  );
}

const DetectionHealth = () => {
  const { isSuperAdmin } = useAuth();
  // Super admins get a second view: the human-detection review study (original vs detected).
  // Everyone else only ever sees the fleet-health dashboard — the toggle isn't rendered for
  // them, and the review API is super-admin gated on the server regardless.
  //
  // The review study is a temporary, capture-heavy observer. It is currently hidden from the UI
  // (flag off) so it can't be re-enabled by mistake; the server-side study flag is the real gate.
  // Flip this to true to bring the "Model review" tab back.
  const SHOW_MODEL_REVIEW = false;
  const [view, setView] = useState("health");
  const [deviceId, setDeviceId] = useState(null);
  const [range, setRange] = useState([dayjs().subtract(7, "day").startOf("day"), dayjs().endOf("day")]);
  const [cameras, setCameras] = useState([]);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    getCameras(1, 1000, {})
      .then((list) => setCameras(Array.isArray(list) ? list : []))
      .catch(() => setCameras([]));
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getDetectionHealthOverview({ deviceId: deviceId || undefined, ...dayWindow(range) });
      setData(res || null);
    } catch (err) {
      console.error("Failed to load detection health", err);
      message.error("Could not load detection health");
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [deviceId, range]);

  useEffect(() => { load(); }, [load]);

  const hasData = (data?.totals?.frames || 0) > 0;
  const c = data?.chiller?.per100 || {};
  const h = data?.human?.per100 || {};
  const tiers = data?.tiers || {};
  const S = data?.signals || {};

  // The "what to do now" list, generated from the live signals so the copy always matches
  // the numbers. Actions with a zero count drop out (no blind cameras -> no dispatch line).
  const actions = [];
  if (data) {
    const worstBlind = (data.worstCameras || []).find((x) => x.issue === "blind");
    if (S.blindCameras > 0) {
      actions.push({
        color: CRIT, who: "Field operations",
        what: `Send a technician to the ${S.blindCameras} blind camera${S.blindCameras > 1 ? "s" : ""}`,
        why: `They return almost no usable photos.${worstBlind ? ` Worst: ${worstBlind.store}${worstBlind.region ? ` (${worstBlind.region})` : ""} at ${worstBlind.usablePct}% usable.` : ""}`,
        impact: S.blindCameras, impactSub: "cameras returning near-zero",
      });
    }
    if (S.overBlurCameras > 0) {
      actions.push({
        color: WARN, who: "Field ops + AI team",
        what: `Investigate the ${S.overBlurCameras} camera${S.overBlurCameras > 1 ? "s" : ""} blurring the whole photo`,
        why: "Whole-frame blur is often glare fooling the privacy model — it destroys photos we could have used, and can't be undone.",
        impact: S.overBlurCameras, impactSub: "cameras over-blurring",
      });
    }
    actions.push({
      color: ACCENT, who: "AI team",
      what: "Finish rolling out the new privacy-blur model (v2)",
      why: "The better model is live but not fully rolled out; the rest of the photos still run on the old, over-eager version.",
      impact: pctOf(S.humanV2Share), impactSub: "of photos on the new model",
    });
    actions.push({
      color: MUTED, who: "Reporting / product",
      what: "Record why each photo was blurred",
      why: "A photo blurred for a person looks identical to a camera that sees nothing, which inflates the 'broken' count and misdirects field visits.",
      impact: num(S.redactedButNoBox), impactSub: "blurred photos miscounted as no-fridge",
    });
  }

  const trend = (data?.trend || []).map((d) => ({
    day: d.day?.slice(5),
    Usable: d.usableRate == null ? null : Math.round(d.usableRate * 100),
    Blurred: d.redactionRate == null ? null : Math.round(d.redactionRate * 100),
  }));

  const columns = [
    {
      title: "Store & device", key: "store",
      render: (_, r) => (
        <div>
          <div style={{ fontWeight: 600 }}>{r.store}</div>
          <Text type="secondary" style={{ fontSize: 12 }}>#{r.deviceId || "—"}</Text>
        </div>
      ),
    },
    { title: "Region", dataIndex: "region", key: "region", render: (v) => v || "—" },
    { title: "Photos (7d)", dataIndex: "total", key: "total", align: "right", render: (v) => num(v) },
    {
      title: "Usable", dataIndex: "usablePct", key: "usablePct", align: "right",
      render: (v) => <span style={{ color: v == null ? MUTED : v >= 50 ? GOOD : v >= 20 ? WARN : CRIT, fontWeight: 600 }}>{v == null ? "—" : `${v}%`}</span>,
    },
    {
      title: "Problem", dataIndex: "issue", key: "issue",
      render: (v) => <Tag color={(ISSUE[v] || {}).color}>{(ISSUE[v] || {}).label || v}</Tag>,
    },
    { title: "Do this", dataIndex: "action", key: "action", render: (v) => <Text type="secondary">{v}</Text> },
  ];

  return (
    <div style={{ padding: 16 }}>
      <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", justifyContent: "space-between", gap: 12, marginBottom: 16 }}>
        <Title level={3} style={{ margin: 0 }}>
          Detection Health
          <InfoHint title="What happens to store-fridge camera photos: how many are usable for compliance, and how many are blurred for privacy — plus what to do about the worst cameras. Live from the detection pipeline." />
        </Title>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
          {isSuperAdmin && SHOW_MODEL_REVIEW && (
            <Segmented
              value={view}
              onChange={setView}
              options={[
                { label: "Fleet health", value: "health" },
                { label: "Model review", value: "review" },
              ]}
            />
          )}
          {view === "health" && (
            <>
              <Select
                allowClear showSearch placeholder="All devices" style={{ width: 200 }}
                value={deviceId} onChange={setDeviceId} optionFilterProp="label"
                options={cameras.map((cam) => ({ value: cam.deviceId, label: cam.shop?.title ? `${cam.shop.title} (#${cam.deviceId})` : (cam.deviceTitle || cam.deviceId) }))}
              />
              <RangePicker value={range} onChange={(r) => r && setRange(r)} allowClear={false} />
            </>
          )}
        </div>
      </div>

      {view === "review" && isSuperAdmin ? (
        <DetectionReview />
      ) : (
      <Spin spinning={loading}>
        {!hasData && !loading ? (
          <Empty description="No photos in this window" />
        ) : (
          <>
            {/* Fleet status */}
            <Text type="secondary" style={{ fontWeight: 600, letterSpacing: 0.4 }}>
              FLEET STATUS — of {S.camerasEvaluated || 0} cameras busy enough to judge
            </Text>
            <Row gutter={[12, 12]} style={{ marginTop: 8, marginBottom: 24 }}>
              <Col xs={24} sm={8}><Tile value={tiers.healthy ?? 0} label="Healthy" sub="returning usable photos" color={GOOD} /></Col>
              <Col xs={24} sm={8}><Tile value={tiers.weak ?? 0} label="Underperforming" sub="usable rate 20–50%" color={WARN} /></Col>
              <Col xs={24} sm={8}><Tile value={tiers.broken ?? 0} label="Effectively blind" sub="under 20% usable — need a visit" color={CRIT} /></Col>
            </Row>

            {/* The two checks */}
            <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
              <Col xs={24} lg={12}>
                <Card title={<span>Out of every 100 photos — how many are usable?<InfoHint title="Usable = the chiller detector cleared the confidence gate (default 0.70), so compliance can run on it." /></span>} size="small">
                  <div style={{ display: "flex", gap: 20, flexWrap: "wrap", alignItems: "center" }}>
                    <Waffle segs={[{ n: c.usable || 0, color: GOOD }, { n: c.barely || 0, color: WARN }, { n: c.nothing || 0, color: EMPTY }]} />
                    <Keys items={[
                      { color: GOOD, big: c.usable ?? 0, label: "Clear enough to use", sub: `${num(data?.chiller?.usable)} photos` },
                      { color: WARN, big: c.barely ?? 0, label: "Only just visible", sub: `${num(data?.chiller?.barely)} photos` },
                      { color: EMPTY, big: c.nothing ?? 0, label: "Nothing usable", sub: `aimed wrong / dark / blocked` },
                    ]} />
                  </div>
                </Card>
              </Col>
              <Col xs={24} lg={12}>
                <Card title={<span>Out of every 100 photos — how many got blurred?<InfoHint title="Photos with a person are blurred at ingest for privacy. Whole-frame blurs cover ≥95% of the image — sometimes a real close-up, sometimes glare fooling the model." /></span>} size="small">
                  <div style={{ display: "flex", gap: 20, flexWrap: "wrap", alignItems: "center" }}>
                    <Waffle segs={[{ n: h.kept || 0, color: GOOD }, { n: h.personBlurred || 0, color: ACCENT }, { n: h.wholeFrame || 0, color: CRIT }]} />
                    <Keys items={[
                      { color: GOOD, big: h.kept ?? 0, label: "Kept as-is", sub: "no person seen" },
                      { color: ACCENT, big: h.personBlurred ?? 0, label: "Person blurred", sub: "covered for privacy" },
                      { color: CRIT, big: h.wholeFrame ?? 0, label: "Whole photo blurred", sub: `${num(data?.human?.wholeFrame)} photos wiped` },
                    ]} />
                  </div>
                </Card>
              </Col>
            </Row>

            {/* What to do now */}
            <Text type="secondary" style={{ fontWeight: 600, letterSpacing: 0.4 }}>WHAT TO DO NOW — in priority order</Text>
            <div style={{ display: "flex", flexDirection: "column", gap: 10, marginTop: 8, marginBottom: 24 }}>
              {actions.map((a, i) => (
                <Card key={i} size="small">
                  <Row align="middle" gutter={12} wrap>
                    <Col flex="none">
                      <div style={{ width: 38, height: 38, borderRadius: 10, background: a.color, color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 700, fontSize: 18 }}>{i + 1}</div>
                    </Col>
                    <Col flex="auto" style={{ minWidth: 220 }}>
                      <Text type="secondary" style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: 0.4 }}>{a.who}</Text>
                      <div style={{ fontWeight: 600, fontSize: 15 }}>{a.what}</div>
                      <Text type="secondary" style={{ fontSize: 13 }}>{a.why}</Text>
                    </Col>
                    <Col flex="none" style={{ textAlign: "right", minWidth: 120 }}>
                      <div style={{ fontSize: 20, fontWeight: 700 }}>{a.impact}</div>
                      <Text type="secondary" style={{ fontSize: 12 }}>{a.impactSub}</Text>
                    </Col>
                  </Row>
                </Card>
              ))}
            </div>

            {/* Fix these first */}
            <Text type="secondary" style={{ fontWeight: 600, letterSpacing: 0.4 }}>FIX THESE CAMERAS FIRST</Text>
            <Table
              style={{ marginTop: 8, marginBottom: 24 }}
              size="small" rowKey="cameraId" pagination={false}
              columns={columns} dataSource={data?.worstCameras || []}
              scroll={{ x: 640 }}
            />

            {/* Trend */}
            <Card title={<span>Usable vs blurred — last 14 days<InfoHint title="Daily share of photos that were compliance-usable, against the share blurred for privacy. Redaction was switched on part-way through, so the two lines start at different points." /></span>} size="small">
              <ResponsiveContainer width="100%" height={280}>
                <LineChart data={trend} margin={{ top: 8, right: 16, bottom: 4, left: -8 }}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} />
                  <XAxis dataKey="day" tick={{ fontSize: 12 }} />
                  <YAxis unit="%" domain={[0, 100]} tick={{ fontSize: 12 }} />
                  <Tooltip formatter={(v) => (v == null ? "—" : `${v}%`)} />
                  <Legend />
                  <Line type="monotone" dataKey="Usable" stroke={GOOD} strokeWidth={2.5} dot={{ r: 3 }} connectNulls />
                  <Line type="monotone" dataKey="Blurred" stroke={ACCENT} strokeWidth={2.5} dot={{ r: 3 }} connectNulls />
                </LineChart>
              </ResponsiveContainer>
            </Card>
          </>
        )}
      </Spin>
      )}
    </div>
  );
};

export default DetectionHealth;
